import { Video, Phone, MoreVertical, X } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'

interface ClientWhatsAppMockupProps {
    clientName: string
    onClose?: () => void
    delay?: number
}

const ClientWhatsAppMockup = ({ clientName, onClose, delay = 0 }: ClientWhatsAppMockupProps) => {
    const [visible, setVisible] = useState(false)
    const [isClosing, setIsClosing] = useState(false)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const dragStart = useRef({ x: 0, y: 0 })

    useEffect(() => {
        const timer = setTimeout(() => {
            setVisible(true)
        }, delay)
        return () => clearTimeout(timer)
    }, [delay])

    // Trigger exit animation before unmounting
    useEffect(() => {
        const closeTimer = setTimeout(() => {
            setIsClosing(true)
        }, 9500 + delay) // 500ms before parent unmounts (assuming 10s total)

        return () => clearTimeout(closeTimer)
    }, [delay])

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true)
        dragStart.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        }
    }

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return
        setPosition({
            x: e.clientX - dragStart.current.x,
            y: e.clientY - dragStart.current.y
        })
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }

    // Touch handlers
    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length !== 1) return
        setIsDragging(true)
        const touch = e.touches[0]
        dragStart.current = {
            x: touch.clientX - position.x,
            y: touch.clientY - position.y
        }
    }

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || e.touches.length !== 1) return
        const touch = e.touches[0]
        setPosition({
            x: touch.clientX - dragStart.current.x,
            y: touch.clientY - dragStart.current.y
        })
    }

    const handleTouchEnd = () => {
        setIsDragging(false)
    }

    useEffect(() => {
        const handleGlobalMouseUp = () => setIsDragging(false)
        window.addEventListener('mouseup', handleGlobalMouseUp)
        return () => window.removeEventListener('mouseup', handleGlobalMouseUp)
    }, [])

    return (
        <div
            className={`absolute w-[110px] md:w-[140px] h-[140px] md:h-[190px] bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-2xl overflow-hidden flex flex-col z-[10006] font-sans border-2 border-green-200/50 transition-all duration-500 ease-out select-none ${visible && !isClosing ? 'opacity-100 translate-y-0 scale-100' :
                isClosing ? 'opacity-0 scale-110 translate-y-[-20px]' : // Pop-out effect
                    'opacity-0 translate-y-10 scale-95'
                }`}
            style={{
                backgroundColor: '#0b141a',
                transform: `translate(${position.x}px, ${position.y}px)`,
                cursor: isDragging ? 'grabbing' : 'auto',
                animation: visible && !isDragging ? 'float 6s ease-in-out infinite' : 'none'
            }}
            onMouseMove={handleMouseMove}
        >
            <style>{`
                @keyframes float {
                    0% { transform: translate(${position.x}px, ${position.y}px); }
                    50% { transform: translate(${position.x}px, ${position.y - 10}px); }
                    100% { transform: translate(${position.x}px, ${position.y}px); }
                }
            `}</style>
            {/* Header */}
            <div
                className="bg-[#008069] p-2 flex items-center justify-between text-white shadow-md z-10 cursor-grab active:cursor-grabbing"
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className="flex items-center gap-2 pointer-events-none">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px]">👤</div>
                    <div className="flex flex-col">
                        <span className="font-semibold text-[10px] leading-tight">{clientName}</span>
                        <span className="text-[8px] text-white/80">online</span>
                    </div>
                </div>
                <div className="flex gap-1 text-white/90 pointer-events-none">
                    <Video size={10} />
                    <Phone size={10} />
                    <MoreVertical size={10} />
                </div>
            </div>

            {/* Chat Area */}
            <div
                className="flex-1 p-2 overflow-y-auto relative"
                style={{
                    backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '300px',
                    backgroundColor: '#0b141a'
                }}
            >
                <div className="bg-[#005c4b] p-1.5 rounded-lg rounded-tr-none shadow-sm max-w-[95%] self-end text-[10px] text-[#e9edef] relative leading-relaxed ml-auto">
                    Cobrança efetuada com sucesso.
                    <span className="inline-block w-[2px] h-2.5 bg-[#34b7f1] ml-1 align-middle"></span>
                    <span className="text-[8px] text-gray-300 block text-right mt-0.5">
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        <span className="ml-1 text-[#53bdeb]">✓✓</span>
                    </span>
                    {/* Bubble Tail */}
                    <div className="absolute top-0 right-[-6px] w-0 h-0 border-t-[8px] border-t-[#005c4b] border-r-[8px] border-r-transparent"></div>
                </div>
            </div>
        </div>
    )
}

export default ClientWhatsAppMockup
